import React from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/sections/Footer";
import { PageMeta, PageHero, Section, SectionHeading, FeatureCard, CtaBlock, Pill } from "@/components/PageShellParts";
import { AlertCircle, Clock, Database, FileCheck, Lock, Target, Users } from "lucide-react";

const Icons = { AlertCircle, Clock, Database, FileCheck, Lock, Target, Users };

export default function PrivacyPage() {
  return (
    <main className="min-h-screen bg-white">
      <Navbar />
      <PageMeta title="Privacy & Data Protection" description="PsyTech's privacy and data protection overview for digital mental health assessment workflows." />
      <div className="pt-20">
      <PageHero
        eyebrow="Privacy"
        title="Privacy & Data Protection"
        subtitle="PsyTech is built with privacy-by-design principles for sensitive psychological assessment workflows."
      >
        <div className="flex flex-wrap gap-2">
          <Pill tone="cyan">Designed with GDPR principles in mind</Pill>
          <Pill tone="cyan">Data minimization</Pill>
          <Pill tone="cyan">Role-based access</Pill>
        </div>
      </PageHero>

      <Section>
        <SectionHeading
          eyebrow="What we may collect"
          title="Data depends on configuration and use case"
          subtitle="Exact fields vary by deployment and agreement. In general, PsyTech may process the following types of data:"
        />
        <div className="grid md:grid-cols-2 gap-4">
          {[
            ["Account and contact information", "Name, email, organization, role — used for authentication, communication, and access control."],
            ["Assessment responses", "Answers provided during structured assessments, used to generate summaries and insights."],
            ["Generated reports and summaries", "AI-assisted structured outputs intended for review by qualified professionals."],
            ["Session information", "Usage context (anonymous visitor/session identifiers, language, device type) used to operate the platform."],
            ["Technical and security logs", "Operational logs used to maintain platform reliability and security."],
            ["Contact form information", "Details submitted via the website contact or pilot inquiry forms."],
          ].map(([title, desc]) => (
            <FeatureCard key={title} icon={Icons.Database} title={title}>{desc}</FeatureCard>
          ))}
        </div>
      </Section>

      <Section className="bg-slate-50">
        <SectionHeading
          eyebrow="Why we use it"
          title="Purposes we consider appropriate"
        />
        <div className="grid md:grid-cols-2 gap-4">
          {[
            ["Provide digital assessments", "Delivering the core assessment experience to users and professionals."],
            ["Generate structured insights", "Organizing responses into professional-review-friendly summaries."],
            ["Support users and professionals", "Helping users navigate the platform and professionals review cases."],
            ["Manage demo and contact requests", "Responding to inquiries, pilot proposals, and partnership discussions."],
            ["Improve platform functionality", "Understanding operational patterns without relying on intrusive tracking."],
            ["Maintain security", "Detecting abuse, protecting data, and preserving service integrity."],
          ].map(([title, desc]) => (
            <FeatureCard key={title} icon={Icons.Target} title={title}>{desc}</FeatureCard>
          ))}
        </div>
      </Section>

      <Section>
        <SectionHeading
          eyebrow="How we protect it"
          title="Technical and organizational measures"
        />
        <div className="grid md:grid-cols-3 gap-4">
          {[
            ["Encrypted connections", "TLS for data in transit across the platform."],
            ["Access controls", "Authentication, session management, and JWT-based admin access."],
            ["Role-based permissions", "Scoped access so users only see what their role requires."],
            ["Secure storage practices", "Modern database hosting with controlled access and audit trails."],
            ["Privacy-conscious analytics", "Cookie-less first-party web analytics; no raw IP addresses retained."],
            ["Ongoing review", "Security is treated as a continuous discipline, not a one-time audit."],
          ].map(([title, desc]) => (
            <FeatureCard key={title} icon={Icons.Lock} title={title}>{desc}</FeatureCard>
          ))}
        </div>
      </Section>

      <Section className="bg-slate-50">
        <SectionHeading
          eyebrow="Who may access data"
          title="Access is scoped by role and necessity"
        />
        <div className="grid md:grid-cols-2 gap-4">
          {[
            ["The user", "Users can view their own interactions, where the deployment supports it."],
            ["Authorized professionals or organizations", "Professionals reviewing a case, within the organization they serve."],
            ["Authorized administrators", "Designated admins within a deployment, with scoped permissions."],
            ["PsyTech support staff", "Only when necessary for technical or security support, and under appropriate controls."],
            ["Processors and service providers", "Infrastructure providers (hosting, email, analytics) acting under appropriate safeguards."],
          ].map(([title, desc]) => (
            <FeatureCard key={title} icon={Icons.Users} title={title}>{desc}</FeatureCard>
          ))}
        </div>
      </Section>

      <Section>
        <SectionHeading
          eyebrow="Your rights"
          title="Rights under data protection law"
          subtitle="Subject to applicable law, individuals may have the following rights. Contact the PsyTech team to make a privacy request."
        />
        <div className="grid md:grid-cols-3 gap-4">
          {[
            "Access your personal data",
            "Correct inaccurate data",
            "Request deletion",
            "Restrict processing",
            "Object to processing",
            "Data portability where applicable",
          ].map((item) => (
            <div
              key={item}
              className="flex items-start gap-3 bg-white border border-slate-200 rounded-xl px-4 py-3"
            >
              <FileCheck className="w-4 h-4 text-cyan-700 mt-0.5 flex-shrink-0" />
              <span className="text-sm text-slate-700">{item}</span>
            </div>
          ))}
        </div>
      </Section>

      <Section className="bg-slate-50">
        <SectionHeading eyebrow="Data retention" title="Only as long as appropriate" />
        <div className="max-w-3xl text-slate-700 leading-relaxed space-y-4">
          <p className="flex items-start gap-3">
            <Clock className="w-5 h-5 text-cyan-700 mt-0.5 flex-shrink-0" />
            <span className="text-sm">
              Retention periods may depend on the organization, the agreement in place, and legal requirements.
              PsyTech aims to avoid unnecessary data retention and to honour deletion requests where appropriate.
            </span>
          </p>
          <p className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
            <span className="text-sm">
              This page is a general privacy overview and does not replace the official legal Privacy Policy or
              Data Processing Agreement that applies to your specific deployment.
            </span>
          </p>
        </div>
      </Section>

      <CtaBlock
        title="Contact us about data protection"
        subtitle="For privacy questions, data requests, or to review documentation before a pilot, reach out to the PsyTech team."
        primaryHref="/#contact"
        primaryLabel="Contact PsyTech"
        secondaryHref="/trust-safety"
        secondaryLabel="Read about responsible AI"
        testid="privacy-cta"
      />

      <div className="max-w-5xl mx-auto px-6 md:px-12 pb-16">
        <div
          className="bg-white border border-slate-200 rounded-2xl p-6 md:p-8"
          data-testid="privacy-data-controller"
        >
          <h3 className="text-lg font-semibold text-slate-900 mb-3 font-['Plus_Jakarta_Sans']">
            Data controller
          </h3>
          <div className="text-sm text-slate-600 leading-relaxed space-y-1">
            <p className="text-slate-800 font-semibold">PsyTech B.V.</p>
            <p>Registered in the Netherlands</p>
            <p>KVK (Dutch Chamber of Commerce): <span className="font-mono text-slate-700">42048210</span></p>
            <p>Contact: <a href="mailto:info@psy-tech.nl" className="text-cyan-700 hover:text-cyan-900">info@psy-tech.nl</a></p>
          </div>
          <p className="text-xs text-slate-500 mt-4 leading-relaxed">
            Where PsyTech B.V. acts as a processor on behalf of a clinic, university, or employer, that organization is the data controller for its own users and PsyTech B.V. processes personal data strictly on their instructions under a Data Processing Agreement.
          </p>
        </div>
      </div>
    </div>
      <div className="bg-slate-50 border-y border-slate-200">
        <div className="max-w-5xl mx-auto px-6 md:px-12 py-6 text-center">
          <p className="text-xs text-slate-500 leading-relaxed">
            <strong className="text-slate-700">Important:</strong> PsyTech provides digital assessment support and educational information only.
            It does not provide diagnosis, treatment, medication advice, or emergency support.
            AI-generated insights should be reviewed by qualified professionals before clinical use.
          </p>
        </div>
      </div>
      <Footer />
    </main>
  );
}
